import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { ParagraphDto } from '../../api/queries/books';
import { useParagraphImages } from '../../api/queries/media';
import { AppImage } from '../common/AppImage';
import { ImageLightbox } from '../common/ImageLightbox';

interface Props {
  paragraphs: ParagraphDto[];
  selectedParagraphId: string | null;
}

/**
 * Right-sidebar preview. Renders paragraphs read-only with inline images + audio. Scrolls
 * to the selected paragraph whenever the selection changes.
 */
export function PreviewPanel({ paragraphs, selectedParagraphId }: Props) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedParagraphId || !container.current) return;
    const target = container.current.querySelector(
      `[data-paragraph-preview="${selectedParagraphId}"]`,
    );
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedParagraphId]);

  return (
    <Paper sx={{ p: 2, maxHeight: '80vh', overflowY: 'auto' }} ref={container}>
      <Typography variant="subtitle2" gutterBottom>
        Preview
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={3}>
        {paragraphs.map((p) => (
          <Box
            key={p.id}
            data-paragraph-preview={p.id}
            sx={{ opacity: p.id === selectedParagraphId ? 1 : 0.75 }}
          >
            <Box dangerouslySetInnerHTML={{ __html: p.contentHtml }} />
            <ParagraphMedia paragraphId={p.id} />
          </Box>
        ))}
        {paragraphs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Preview is empty. Add paragraphs to this chapter to see the reader
            view.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function ParagraphMedia({ paragraphId }: { paragraphId: string }) {
  const { data } = useParagraphImages(paragraphId);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  if (!data || data.length === 0) return null;

  const lightboxImages = data.map((ref) => ({
    src: ref.url,
    alt: `Paragraph image`,
  }));

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {data.map((ref, index) => (
        <AppImage
          key={ref.referenceId}
          src={ref.thumbnailUrl ?? ref.url}
          width="100%"
          objectFit="contain"
          objectPosition="center"
          onClick={() => setLightboxIndex(index)}
        />
      ))}
      <ImageLightbox
        open={lightboxIndex >= 0}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
      />
    </Stack>
  );
}
